import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

interface EmailGateProps {
  onSubmit: (email: string) => void;
  submitting: boolean;
  error: string | null;
}

// Identity check before any form content is revealed: the visitor enters the
// email the invitation was sent to. The backend verifies it (case-insensitive)
// and only then returns the form.
export function EmailGate({ onSubmit, submitting, error }: EmailGateProps) {
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) onSubmit(email.trim());
  };

  return (
    <div className="mx-auto w-full max-w-[440px] px-4 py-16">
      <div className="rounded-2xl border border-border bg-card p-7 shadow-sm">
        <div className="mb-5 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-hygge/10">
            <ShieldCheck className="h-6 w-6 text-hygge" />
          </div>
          <h1 className="text-lg font-semibold tracking-tight">Verify it&apos;s you</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the email address this form was sent to so we can confirm your identity.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="preboarding-email">Email address</Label>
            <Input
              id="preboarding-email"
              type="email"
              autoFocus
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error ? true : undefined}
              className="focus-visible:ring-hygge focus-visible:border-hygge"
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={!email.trim() || submitting}
            className="w-full bg-cta hover:bg-cta/90 text-cta-foreground font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
